import React from 'react';
import { Avatar } from '../common/Avatar';
import { TeamBadge } from '../common/TeamBadge';
import { Sparkline } from '../common/Sparkline';
import { answerRateTone, formatDuration, formatNumber, formatPercent } from '../../utils/format';
import type { TeamFilter, TeamSummary } from '../CallsInsightHub/useCallsInsightHub';
interface ManagerTableProps {
  summaries: TeamSummary[];
  activeTeam: TeamFilter;
  onTeamChange: (team: TeamFilter) => void;
}
const headers = ['Sales manager', 'Calls made', 'Answered', 'Missed', 'Answer rate', 'Avg call length', 'Total talk time', 'Trend'];
export function ManagerTable({
  summaries,
  activeTeam,
  onTeamChange
}: ManagerTableProps) {
  return <section aria-labelledby="managers-heading" className="rounded-2xl border border-line bg-surface shadow-card" data-id="element-492" data-mp="1dvwx5243a0qr">
      <div className="flex items-baseline justify-between px-6 pb-3 pt-5" data-id="element-493" data-mp="1ivhyfj6qel6n">
        <h2 id="managers-heading" className="text-base font-semibold text-ink" data-id="element-494" data-mp="1mvsc7i0rmfw2">
          By sales manager
        </h2>
        <p className="text-xs text-subtle" data-id="element-495" data-mp="29sn6dzwhvqd1">Select a row to filter by team</p>
      </div>
      <div className="overflow-x-auto" data-id="element-496" data-mp="24sr17xc29svw">
        <table className="w-full min-w-[900px] text-sm" data-id="element-497" data-mp="24rz7ks20y589">
          <thead data-id="element-498" data-mp="1zto0gp3468tq">
            <tr className="border-y border-line bg-canvas/60 text-xs text-muted" data-id="element-499" data-mp="1zv8veftcbts4">
              {headers.map((h, i) => <th key={h} scope="col" className={`px-6 py-2.5 font-medium ${i === 0 ? 'text-left' : i === 7 ? 'text-left' : 'text-right'}`} data-id="element-500" data-mp="1uwxh9sw0ix43" data-mpi={i}>
                  {h}
                </th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-line" data-id="element-501" data-mp="2iss4tkjpnef2">
            {summaries.map(({
            team,
            manager,
            members,
            stats,
            series,
            trend
          }) => {
            const active = activeTeam === team;
            const dimmed = activeTeam !== 'All' && !active;
            return <tr key={team} tabIndex={0} aria-pressed={active} onClick={() => onTeamChange(active ? 'All' : team)} onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onTeamChange(active ? 'All' : team);
              }
            }} className={`cursor-pointer transition-[background-color,opacity] duration-150 ease-out hover:bg-canvas/70 focus:outline-none focus-visible:bg-canvas ${dimmed ? 'opacity-50' : ''} ${active ? 'bg-accent-soft/40' : ''}`} data-id="element-502" data-mp="3bbfmepzi0bbe">
                  <td className="px-6 py-3.5" data-id="element-503" data-mp="bwojhf4a53qa">
                    <div className="flex items-center gap-3" data-id="element-504" data-mp="bxzn9exq6bhe">
                      <Avatar name={manager} solid data-id="element-505" data-mp="guzv9ve4xob5" />
                      <div className="leading-tight" data-id="element-506" data-mp="gxlzpm5qbnp5">
                        <p className="whitespace-nowrap font-semibold text-ink" data-id="element-507" data-mp="26a668zwv1wk9">{manager}</p>
                        <div className="mt-1 flex items-center gap-2" data-id="element-508" data-mp="2b9sltg05vow8">
                          <TeamBadge team={team} data-id="element-509" data-mp="3vrl0xpsr5wfg" />
                          <span className="whitespace-nowrap text-xs text-muted" data-id="element-510" data-mp="3vscxp3y3607b">{members} users</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3.5 text-right font-medium text-ink" data-id="element-511" data-mp="9djpf8ky3tpv">{formatNumber(stats.outbound)}</td>
                  <td className="px-6 py-3.5 text-right text-ink" data-id="element-512" data-mp="4jyyu1lom20p">{formatNumber(stats.inbound)}</td>
                  <td className="px-6 py-3.5 text-right font-medium text-danger" data-id="element-513" data-mp="iau5r8cme91n">{formatNumber(stats.missed)}</td>
                  <td className={`px-6 py-3.5 text-right font-semibold ${answerRateTone(stats.answerRate)}`} data-id="element-514" data-mp="dfy89som6th5">{formatPercent(stats.answerRate)}</td>
                  <td className="px-6 py-3.5 text-right text-ink" data-id="element-515" data-mp="nct6eagz6ruw">{formatDuration(stats.avgSeconds)}</td>
                  <td className="px-6 py-3.5 text-right font-bold text-ink" data-id="element-516" data-mp="2u397rvkuxxnb">{formatDuration(stats.talkSeconds)}</td>
                  <td className="px-6 py-3.5" data-id="element-517" data-mp="2p4pj3nevq7r9">
                    <Sparkline data={series} tone={trend >= 0 ? 'positive' : 'danger'} data-id="element-518" data-mp="1g6g6wwet7an5" />
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </section>;
}